import { widgetFilterSchema } from '../../common/schema'
import { okOrThrow } from '../../common/http'
import { homerunnerApiBaseUrl } from '../../hr'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { z } from 'zod'

export const galleryParamsSchema = widgetFilterSchema.extend({
  // Required filters (at least one must be provided). Gallery IS about images,
  // so it keeps the image-specific copy the base schema dropped in #687.
  feed_id: z.number().int().optional().describe('Filter images by feed ID'),
  property: z.string().describe('Filter images by property slug'),
  platform: z.string().nullish().describe('Filter images by platform UUID'),
  
  // Optional parameters
  limit: z.number().int().min(1).max(100).default(50).describe('Number of images per page (default: 50)'),
  page: z.number().int().min(1).default(1).describe('Page number for pagination'),
  // property_id: z.number().int().optional().describe('Filter by specific property ID'),
  position_min: z.number().int().min(0).optional().describe('Minimum position filter'),
  position_max: z.number().int().min(0).optional().describe('Maximum position filter'),
}).refine(
  (data) => data.feed_id || data.property || data.platform,
  {
    message: 'At least one of feed_id, property, or platform must be provided',
  }
).describe('Data filter options for gallery images')

export type PropertyImage = {
  id: number
  url: string
  caption: string | null
  position: number
  /**
   * The room a photo belongs to (#1694). Central holds these since #3626 but
   * the public API does not serialize them yet (central#3703): every field is
   * optional and the gallery treats absent and null alike (no room). The
   * grouping key is room_uid; names repeat within a listing.
   */
  room_uid?: string | null
  room_name?: string | null
  room_type?: string | null
  room_position?: number | null
  // property: {
  //   uuid: string
  //   name: string
  // }
}

export type GalleryFilter = z.infer<typeof galleryParamsSchema>

/** Most property-images pages the gallery loads after mount (#1810): 10 x 50 = 500 photos. */
export const GALLERY_MAX_PAGES = 10

export async function fetchGalleryImages(filter: GalleryFilter) {
  const url = new URL(`${homerunnerApiBaseUrl()}/public-api/v1/property-images`)
  Object.entries(filter).forEach(([key, value]) => {
    if (value !== undefined && value !== null) url.searchParams.append(key, String(value))
  })
  const res = await fetch(url.toString())
  await okOrThrow(res, 'Failed to fetch gallery images')
  const data = await res.json()

  return {
    ...data,
  } as {
    // Errors never reach this cast (guarded by res.ok above); canonical error
    // bodies carry `success: false` instead of `status` (central #2756).
    status: 'ok'
    result: PropertyImage[]
    pagination: {
      page: number
      pageSize: number
      totalPages: number
      totalItems: number
      hasNextPage: boolean
      hasPrevPage: boolean
    }
  }
}

export function useFetchGalleryImages(filter: GalleryFilter) {
  return useInfiniteQuery({
    queryKey: ['gallery-images', filter],
    queryFn: ({ pageParam = 1 }) => fetchGalleryImages({ ...filter, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage?.pagination?.hasNextPage) {
        return lastPage.pagination.page + 1
      }
      return undefined
    },
  })
}

/**
 * The gallery's query, plus the chain that loads the rest of the photos (#1810).
 *
 * Page 1 carries SSR and first paint exactly as before (ssr.ts prefetches it;
 * nothing here runs on the server). Once mounted, the gallery asks for the
 * next page through the same fetcher until `hasNextPage` is false, so every
 * reader of the `['gallery-images', filter]` entry (the hero grid, the room
 * tour, the lightbox, the Spaces widget's passive observer) re-derives from
 * the full set as pages land. On amber-sunset (203 photos) the bedrooms all
 * sit past the first 50, which is why the room fill saw none of them.
 *
 * One chain per mount and per key: one request in flight at a time, driven by
 * React Query's own state. The gate is `isFetching` (ANY fetch on the entry),
 * not `isFetchingNextPage`: a stale page 1 refetches on mount, and a
 * fetchNextPage issued during that refetch joins it (`cancelRefetch: false`)
 * and resolves with page 1 again, with no dependency left to re-run the
 * effect; waiting for `isFetching` to clear is what keeps the chain alive
 * past that first refetch. `cancelRefetch: false` also makes a Strict-Mode
 * double effect join the in-flight page instead of restarting it. A failed
 * page ends the chain: the entry keeps the pages it has and `isError` stops
 * the effect, since React Query has already applied its retry policy by
 * then. GALLERY_MAX_PAGES bounds a server that keeps answering "next page";
 * hitting it logs once.
 *
 * Call it from ONE component per gallery (GalleryStyle). Every other reader
 * stays on useFetchGalleryImages, which is the same observer without the
 * effect.
 */
export function useFetchAllGalleryImages(filter: GalleryFilter) {
  const query = useFetchGalleryImages(filter)
  const { data, hasNextPage, isFetching, isError, fetchNextPage } = query
  const loaded = data?.pages?.length ?? 0
  const capLogged = useRef(false)

  useEffect(() => {
    if (!hasNextPage || isFetching || isError || loaded === 0) return
    if (loaded >= GALLERY_MAX_PAGES) {
      if (!capLogged.current) {
        capLogged.current = true
        console.warn(
          `[homerunner gallery] stopped loading property-images after ${GALLERY_MAX_PAGES} pages ` +
            `(${loaded * (filter.limit ?? 50)} photos); the listing has more. Raise GALLERY_MAX_PAGES to show them.`,
        )
      }
      return
    }
    void fetchNextPage({ cancelRefetch: false })
  }, [hasNextPage, isFetching, isError, loaded, fetchNextPage, filter.limit])

  return query
}
